/**
 * Nakama Match Module
 * Handles match operations like creating, joining, and sending match messages
 */

import type { Socket, Match } from '@heroiclabs/nakama-js';
import { nakamaClientManager } from './nakama-client';

/**
 * Nakama Match Manager
 * Manages match-related operations
 */
class NakamaMatchManager {
  private socket: Socket | null = null;

  /**
   * Set the socket instance (called by NakamaService)
   */
  setSocket(socket: Socket | null): void {
    this.socket = socket;
  }

  /**
   * Get the current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Create game match via RPC
   * Calls the server's create_game_match RPC endpoint
   */
  async createGameMatch(
    gameName: string,
    worldName: string,
    opts?: { sessionId?: string },
  ): Promise<{ match_id: string; game_name: string; world_name: string }> {
    const session = nakamaClientManager.getSession();
    if (!session) {
      throw new Error('Must be authenticated before creating a match');
    }

    const client = nakamaClientManager.getClient();
    
    try {
      console.log('[NakamaMatch] Creating game match:', { gameName, worldName, ...opts });

      const payload: Record<string, unknown> = {
        game_name: gameName,
        world_name: worldName,
      };
      if (opts?.sessionId) {
        payload.session_id = opts.sessionId;
      }

      const response = await client.rpc(session, 'create_game_match', payload);

      // The match to join; the server refuses as an error, by its code, and
      // never answers a match it has not got.
      const result = typeof response.payload === 'string' 
        ? JSON.parse(response.payload) 
        : response.payload;

      console.log('[NakamaMatch] Match created successfully:', result.match);
      return result.match;
    } catch (error) {
      // Refused as it came — a refused call is nakama-js's Response — for the
      // way in to read: its code, or a gateway's wordless answer
      // (domains/join-refusals.ts, readJoinRefusal).
      console.warn('[NakamaMatch] create_game_match refused:', error);
      throw error;
    }
  }

  /**
   * Join a match via socket
   * 
   * @param matchId - Match ID to join
   * @param metadata - Optional metadata passed to the server's MatchJoinAttempt handler
   * @returns Match object
   */
  async joinMatch(matchId: string, metadata?: Record<string, string>): Promise<Match> {
    if (!this.socket) {
      throw new Error('Socket not created. Call createSocket() first');
    }

    const session = nakamaClientManager.getSession();
    if (!session) {
      throw new Error('Must be authenticated before joining a match');
    }

    try {
      console.log('[NakamaMatch] Joining match:', matchId, metadata ? `(metadata: ${JSON.stringify(metadata)})` : '');
      const match = await this.socket.joinMatch(matchId, undefined, metadata);
      console.log('[NakamaMatch] Successfully joined match');
      console.log('[NakamaMatch] Match object keys:', Object.keys(match));
      console.log('[NakamaMatch] Match.self:', match.self);
      console.log('[NakamaMatch] Match.presences:', match.presences);
      
      // Initialize presences with self if presences array doesn't exist
      if (!match.presences && match.self) {
        console.log('[NakamaMatch] No presences array, creating with self');
        match.presences = [match.self];
      }
      
      return match;
    } catch (error) {
      console.error('[NakamaMatch] Failed to join match:', error);
      throw error;
    }
  }

  /**
   * Reset the match manager
   */
  reset(): void {
    this.socket = null;
    console.log('[NakamaMatch] Match manager reset');
  }
}

// Export singleton instance
export const nakamaMatchManager = new NakamaMatchManager();
